//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import {
  DataGridPremium,
  GRID_AGGREGATION_FUNCTIONS,
  GridAggregationFunction,
  GridColDef,
  GridColumnOrderChangeParams,
  GridColumnResizeParams,
  GridColumnVisibilityModel,
  GridSortModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid-premium'

import React, { ReactElement, useState } from 'react'

import { Box, CircularProgress, Typography } from '@mui/material'

import { ReportDefinitionCTO } from '@shared/ReportDefinitionCTO'

import { ReportDataShape } from '@shared/Enums'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { ReportRowGeneratorClass } from './ReportRowGeneratorClass'
import { ethers } from 'ethers'

dayjs.extend(utc)
dayjs.extend(timezone)

type ReportExplorerProps = {
  reportRowGeneratorClass: ReportRowGeneratorClass
  reportDefinition?: ReportDefinitionCTO

  onReportDefinitionChange?: (params: ReportDefinitionCTO) => void
}

export function ReportExplorer(props: ReportExplorerProps): React.ReactElement {
  const { reportDefinition: reportDefinitionFromProps, reportRowGeneratorClass, onReportDefinitionChange } = props

  const reportDefinition: ReportDefinitionCTO = reportDefinitionFromProps ?? {
    reportName: 'Embedded',
    reportUuid: ethers.uuidV4(ethers.randomBytes(16)),
    reportDescriptorKey: 'UNSET',
    timestamp: Date.now(),
    dataShape: reportRowGeneratorClass.reportDescriptor?.dataShape ?? ReportDataShape.UNKNOWN,
    accountAddr: ethers.ZeroAddress,
  }
  const rows = reportRowGeneratorClass.rows

  const [columns, _setColumns] = useState<GridColDef[]>(
    reportRowGeneratorClass.getColumnsWithWidths(
      reportDefinition?.customColumnWidths ?? reportRowGeneratorClass.defaultColumnWidths
    )
  )

  const [loadingProgress, _setLoadingProgress] = useState<string | undefined>(undefined)
  const [fetchDataErrorMessage, setFetchDataErrorMessage] = useState<string | undefined>(undefined)
  const reportDescriptor = reportRowGeneratorClass.reportDescriptor

  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(
    reportDefinition?.customColumnWidths ?? reportRowGeneratorClass.defaultColumnWidths
  )
  //const [columnOrder, setColumnOrder] = useState<Record<string, number>>(reportRowGeneratorClass.columnOrder)
  const [sortModel, setSortModel] = useState<{ field: string; sort: 'asc' | 'desc' | null | undefined }[]>(
    reportDefinition?.customSortModel ?? reportRowGeneratorClass.defaultSortModel
  )
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<Record<string, boolean>>(
    reportDefinition?.customColumnVisibilityModel ?? reportRowGeneratorClass.defaultColumnVisibilityModel
  )

  reportRowGeneratorClass.errorMessageCallback = (errorMessage: string) => {
    setFetchDataErrorMessage(errorMessage)
  }
  reportRowGeneratorClass.infoMessageCallback = (infoMessage: string) => {
    _setLoadingProgress(infoMessage)
  }
  function CustomToolbar(): ReactElement {
    return (
      <>
        <GridToolbarContainer
          data-testid="GridToolbarContainer1"
          style={{ float: 'right', padding: '15px' }}
        ></GridToolbarContainer>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              marginTop: 2,
              alignItems: 'center',
              padding: '0 15px 10px 17px',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {reportDescriptor.dataShape !== ReportDataShape.SIERRA_EVENTS && (
                <GridToolbarFilterButton
                  slotProps={
                    {
                      button: {
                        startIcon: <FilterAltOutlinedIcon />,
                        style: {
                          height: '36px',
                          color: '#171717',
                          textTransform: 'none',
                          backgroundColor: '#EEEEEE',
                          padding: '0 12px',
                        },
                      },
                    } as any
                  }
                />
              )}
              <GridToolbarColumnsButton
                slotProps={{
                  button: {
                    style: {
                      height: '36px',
                      color: '#171717',
                      textTransform: 'none',
                      backgroundColor: '#EEEEEE',
                      padding: '0 12px',
                    },
                  },
                }}
              />
              <GridToolbarQuickFilter />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <GridToolbarExport
                excelOptions={{}}
                csvOptions={{}}
                slotProps={{
                  button: {
                    style: {
                      height: '36px',
                      color: '#171717',
                      textTransform: 'none',
                      backgroundColor: '#EEEEEE',
                      padding: '0 12px',
                    },
                  },
                }}
              />
            </Box>
          </Box>

          {reportRowGeneratorClass.loading && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px 0',
              }}
            >
              <Box>
                <CircularProgress />
              </Box>
              <Typography textAlign={'center'} variant="bodyMuted" sx={{ mt: 1 }}>
                Loading Report Data
              </Typography>
              {loadingProgress && (
                <Typography textAlign={'center'} variant="bodyMuted" sx={{ mt: 0.5 }}>
                  {loadingProgress}
                </Typography>
              )}
            </Box>
          )}
          {fetchDataErrorMessage && (
            <Typography textAlign={'center'} variant="bodyMuted" sx={{ mt: 1, color: '#ef4444' }}>
              {fetchDataErrorMessage}
            </Typography>
          )}
        </Box>
      </>
    )
  }

  function onColumnVisibilityModelChange(params: GridColumnVisibilityModel): void {
    setColumnVisibilityModel(params)
    reportDefinition.customColumnVisibilityModel = params
    if (onReportDefinitionChange) {
      onReportDefinitionChange(reportDefinition)
    }
  }
  function onSortModelChange(params: GridSortModel): void {
    const _customSortModel = params.map((param) => ({ field: param.field, sort: param.sort }))
    setSortModel(_customSortModel)
    reportDefinition.customSortModel = _customSortModel
    if (onReportDefinitionChange) {
      onReportDefinitionChange(reportDefinition)
    }

    // setSortModel(params)
  }
  function onColumnWidthChange(params: GridColumnResizeParams): void {
    const prop = params.colDef.field
    const width = params.width
    reportDefinition.customColumnWidths = { ...reportDefinition.customColumnWidths, [prop]: width }
    setColumnWidths({ ...columnWidths, [prop]: width })
    if (onReportDefinitionChange) {
      onReportDefinitionChange(reportDefinition)
    }
    // setColumnWidths(params)
  }
  function onColumnOrderChange(_params: GridColumnOrderChangeParams): void {
    // setColumnOrder(params)
  }

  const modifiedRows = rows?.map((row) => ({
    ...row,
    assets: isNaN(row?.assets) ? 0 : row.assets,
  }))

  const sumErc20Aggregation: GridAggregationFunction<string | number, string | number | null> = {
    apply: (params) => {
      if (params.values.length === 0) {
        return ''
      }

      const vals: string[] = (params.values as string[]) ?? []
      const sum = vals
        .map((val: string) => BigInt(val))
        .reduce((acc: bigint, val: bigint) => {
          return acc + val
        }, BigInt(0))
        .toString()

      return sum
    },
    label: 'Sum ERC20',
    columnTypes: ['erc20'],
  }
  const aveErc20Aggregation: GridAggregationFunction<string | number, string | number | null> = {
    apply: (params) => {
      if (params.values.length === 0) {
        return ''
      }

      const vals: string[] = (params.values as string[]) ?? []
      const sum = vals
        .map((val: string) => BigInt(val))
        .reduce((acc: bigint, val: bigint) => {
          return acc + val
        }, BigInt(0))

      const ave: bigint = vals.length > 0 ? sum / BigInt(vals.length) : BigInt(0)
      return ave.toString()
    },
    label: 'Ave ERC20',
    columnTypes: ['erc20'],
  }

  return (
    <DataGridPremium
      rows={modifiedRows}
      columns={columns}
      getRowHeight={() => 'auto'}
      sortModel={sortModel}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={onColumnVisibilityModelChange}
      onSortModelChange={onSortModelChange}
      onColumnWidthChange={onColumnWidthChange}
      onColumnOrderChange={onColumnOrderChange}
      sx={{
        fontSize: 12,
        fontWeight: 500,
        '& .MuiDataGrid-filler': {
          backgroundColor: '#f5f5f5 !important',
          color: '#000000 !important',
          fontWeight: 600,
          '& *': {
            color: '#000000 !important',
            backgroundColor: 'transparent !important',
          },
        },
        '& .MuiDataGrid-columnHeader': {
          backgroundColor: '#f5f5f5 !important',
          color: '#000000 !important',
          fontWeight: 600,
          '& *': {
            color: '#000000 !important',
            backgroundColor: 'transparent !important',
          },
        },

        '& .MuiDataGrid-columnHeader--sortable': {
          backgroundColor: '#f5f5f5 !important',
          color: '#000000 !important',
          fontWeight: 600,
          '& *': {
            color: '#000000 !important',
            backgroundColor: 'transparent !important',
          },
        },

        '& .MuiDataGrid-columnHeaderTitle': {
          color: '#000000 !important',
          fontWeight: 600,
          backgroundColor: 'transparent !important',
        },

        '& .MuiDataGrid-columnHeaderTitleContainer': {
          color: '#000000 !important',
          backgroundColor: 'transparent !important',
          '& *': {
            color: '#000000 !important',
            backgroundColor: 'transparent !important',
          },
        },

        '& .MuiDataGrid-sortIcon': {
          color: '#000000 !important',
        },

        '& .MuiDataGrid-menuIconButton': {
          color: '#000000 !important',
        },

        '& .MuiDataGrid-iconButtonContainer': {
          '& *': {
            color: '#000000 !important',
            backgroundColor: 'transparent !important',
          },
        },

        '& .MuiDataGrid-columnHeaderTitleContainerContent': {
          color: '#000000 !important',
          backgroundColor: 'transparent !important',
          '& *': {
            color: '#000000 !important',
            backgroundColor: 'transparent !important',
          },
        },

        '& .MuiDataGrid-columnHeaderDraggableContainer': {
          color: '#000000 !important',
          backgroundColor: 'transparent !important',
          '& *': {
            color: '#000000 !important',
            backgroundColor: 'transparent !important',
          },
        },
      }}
      slotProps={{ toolbar: { csvOptions: { allColumns: true } } }}
      slots={{
        toolbar: CustomToolbar,
        openFilterButtonIcon: FilterAltOutlinedIcon,
      }}
      getDetailPanelHeight={() => 'auto'}
      aggregationFunctions={{
        ...GRID_AGGREGATION_FUNCTIONS,
        sumErc20: sumErc20Aggregation,
        aveErc20: aveErc20Aggregation,
      }}
    />
  )
}
